import React, { useState, useEffect } from 'react';
import { BookOpen, Award, ExternalLink, Download, Copy, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { authAxios } from '../../services/crmApi';

interface Props {
  applicationId: number;
}

interface CrmCourse {
  id: number;
  course_name: string;
  has_certificate: boolean;
  completion_year: number;
  platform: string;
  registry_number: string;
  score_percent: number;
  cert_id: number;
  source: string;
  gdrive_cert_url: string;
  gdrive_zaświadczenie_url: string;
  created_at: string;
}

interface LiveEnrollment {
  course_id: number;
  course_name: string;
  enrolled_at: string;
  completed: boolean;
  certificate_issued: boolean;
  progress_percent: number;
  platform_prefix: string;
  registry_number: string;
  score_percent: number;
  cert_id: number;
}

const PLATFORM_LABELS: Record<string, string> = {
  kursy: 'Fundacja NH',
  adk: 'Akademia Kompetencji',
  asp: 'Aspire Media',
};

const PLATFORM_COLORS: Record<string, string> = {
  kursy: 'bg-blue-100 text-blue-700',
  adk: 'bg-purple-100 text-purple-700',
  asp: 'bg-orange-100 text-orange-700',
  'Fundacja NH': 'bg-blue-100 text-blue-700',
  'Akademia Kompetencji': 'bg-purple-100 text-purple-700',
  'Aspire Media': 'bg-orange-100 text-orange-700',
};

export function CompletedCoursesPanel({ applicationId }: Props) {
  const [loading, setLoading] = useState(false);
  const [crmCourses, setCrmCourses] = useState<CrmCourse[]>([]);
  const [liveEnrollments, setLiveEnrollments] = useState<LiveEnrollment[]>([]);
  const [copied, setCopied] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/api/crm/applications/${applicationId}/completed-courses`);
      setCrmCourses(res.data.crmCourses || []);
      setLiveEnrollments(res.data.liveEnrollments || []);
      setLoaded(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCourses(); }, [applicationId]);

  const copyCourseSummary = () => {
    const lines: string[] = [];
    if (crmCourses.length > 0) {
      lines.push('=== Ukonczone kursy (CRM) ===');
      for (const c of crmCourses) {
        lines.push(`- ${c.course_name} | ${c.platform || 'manual'} | ${c.completion_year} | Wynik: ${c.score_percent ? Math.round(c.score_percent) + '%' : 'b/d'} | Cert: ${c.registry_number || 'brak'}`);
      }
    }
    if (liveEnrollments.length > 0) {
      lines.push('\n=== Zapisy na platformach ===');
      for (const e of liveEnrollments) {
        const status = e.certificate_issued ? 'Certyfikat' : e.completed ? 'Ukonczony' : `W trakcie (${e.progress_percent || 0}%)`;
        lines.push(`- ${e.course_name} | ${PLATFORM_LABELS[e.platform_prefix] || e.platform_prefix} | ${status}`);
      }
    }
    if (lines.length === 0) lines.push('Brak kursów');
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalCourses = crmCourses.length + liveEnrollments.filter(e => !crmCourses.some(c => c.cert_id === e.cert_id && e.cert_id)).length;

  if (!loaded && loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 size={16} className="animate-spin" /> Ładowanie kursów...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-indigo-600" />
          <span className="font-semibold text-sm text-gray-800">Kursy i certyfikaty</span>
          {totalCourses > 0 && (
            <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">{totalCourses}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={copyCourseSummary} className="p-1 hover:bg-white/60 rounded" title="Kopiuj podsumowanie">
            {copied ? <CheckCircle size={14} className="text-green-600" /> : <Copy size={14} className="text-gray-500" />}
          </button>
          <button onClick={fetchCourses} className="p-1 hover:bg-white/60 rounded" title="Odswiez" disabled={loading}>
            <RefreshCw size={14} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
        {crmCourses.length === 0 && liveEnrollments.length === 0 ? (
          <p className="text-gray-400 text-xs text-center py-2">Brak kursów dla tego klienta</p>
        ) : (
          <>
            {/* CRM synced courses */}
            {crmCourses.map(c => (
              <div key={`crm-${c.id}`} className="border border-gray-100 rounded-lg p-2.5 space-y-1 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-800 leading-tight">{c.course_name}</p>
                  {c.has_certificate && <Award size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />}
                </div>
                <div className="flex flex-wrap gap-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PLATFORM_COLORS[c.platform] || 'bg-gray-100 text-gray-600'}`}>
                    {c.platform || 'manual'}
                  </span>
                  {c.score_percent && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                      {Math.round(c.score_percent)}%
                    </span>
                  )}
                  {c.source === 'auto' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-700">auto-sync</span>
                  )}
                  <span className="text-[10px] text-gray-400">{c.completion_year}</span>
                </div>
                {c.registry_number && (
                  <p className="text-[10px] text-gray-500 font-mono">{c.registry_number}</p>
                )}
                {(c.gdrive_cert_url || c.gdrive_zaświadczenie_url) && (
                  <div className="flex gap-2 mt-1">
                    {c.gdrive_zaświadczenie_url && (
                      <a href={c.gdrive_zaświadczenie_url} target="_blank" rel="noopener noreferrer"
                         className="text-[10px] text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5">
                        <ExternalLink size={10} /> Zaświadczenie
                      </a>
                    )}
                    {c.gdrive_cert_url && (
                      <a href={c.gdrive_cert_url} target="_blank" rel="noopener noreferrer"
                         className="text-[10px] text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5">
                        <ExternalLink size={10} /> Certyfikat
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Live enrollments not yet in CRM */}
            {liveEnrollments
              .filter(e => !crmCourses.some(c => c.cert_id === e.cert_id && e.cert_id))
              .map(e => (
                <div key={`live-${e.platform_prefix}-${e.course_id}`} className="border border-dashed border-gray-200 rounded-lg p-2.5 space-y-1">
                  <p className="text-sm font-medium text-gray-700 leading-tight">{e.course_name}</p>
                  <div className="flex flex-wrap gap-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PLATFORM_COLORS[e.platform_prefix] || 'bg-gray-100 text-gray-600'}`}>
                      {PLATFORM_LABELS[e.platform_prefix] || e.platform_prefix}
                    </span>
                    {e.certificate_issued ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex items-center gap-0.5">
                        <Award size={8} /> Certyfikat
                      </span>
                    ) : e.completed ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Ukonczony</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                        W trakcie {e.progress_percent ? `(${e.progress_percent}%)` : ''}
                      </span>
                    )}
                  </div>
                  {e.registry_number && (
                    <p className="text-[10px] text-gray-500 font-mono">{e.registry_number}</p>
                  )}
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}
